import { FC } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ScoredCode } from '@stores/QuestionnaireAdminStore';
import { useScoredCodesLinkController } from './useScoredCodesLinkController';

const ScoredCodesTable: FC<{ title: string; codes: ScoredCode[]; emptyText: string }> = ({
  title,
  codes,
  emptyText,
}) => (
  <>
    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
      {title}
    </Typography>
    {codes.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    ) : (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Scoring</TableCell>
            <TableCell>Needs</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {codes.map((scoredCode) => (
            <TableRow key={scoredCode.code}>
              <TableCell sx={{ fontFamily: 'monospace' }}>{scoredCode.code}</TableCell>
              <TableCell>{scoredCode.name}</TableCell>
              <TableCell>
                {scoredCode.requirements.map((requirement) => (
                  <Typography key={requirement} variant="body2">
                    {requirement}
                  </Typography>
                ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </>
);

/** A link, for a code field's helper text, that lists the questionnaire and instrument codes the server scores. */
export const ScoredCodesLink: FC = () => {
  const scoredCodes = useScoredCodesLinkController();

  return (
    <>
      <Link component="button" type="button" variant="caption" onClick={scoredCodes.handleOpen}>
        Codes with scoring
      </Link>
      <Dialog open={scoredCodes.open} onClose={scoredCodes.handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Codes With Scoring</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A questionnaire or instrument is scored only when its code matches one of these exactly, and it meets what
            that scoring needs. Codes lock once published, so set them before the first publish.
          </DialogContentText>
          {scoredCodes.loading && <CircularProgress sx={{ mt: 2 }} />}
          {scoredCodes.loadFailed && (
            <Alert severity="error" sx={{ mt: 2 }}>
              The scored codes could not be loaded.
            </Alert>
          )}
          {!scoredCodes.loading && !scoredCodes.loadFailed && (
            <>
              <ScoredCodesTable
                title="Instrument codes"
                codes={scoredCodes.instruments}
                emptyText="No instruments are scored yet."
              />
              <ScoredCodesTable
                title="Questionnaire codes"
                codes={scoredCodes.questionnaires}
                emptyText="No questionnaire-level scoring yet. Instruments are still scored on their own."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={scoredCodes.handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
